import React, { useMemo } from 'react';
import {
  buildConnectionTrend,
  chartXAtPeriodIndex,
  findCurrentPeriodIndex,
  formatTrendPeriodTooltip,
  getChartAxisEndpointLabels,
  getTrendBucketMode,
  getTrendChartUnitLabel,
  type ConnectionTrendPoint,
} from '../../lib/connectionDashboard';
import { useDashboardTrendDays, useSyncedChartHover } from '../../context/DashboardChartHoverContext';
import ChartTodayMarker from './ChartTodayMarker';
import ChartHoverHighlight from './ChartHoverHighlight';

type ConnectionTrendChartProps = {
  createdAtValues: string[];
  className?: string;
};

const CHART_WIDTH = 400;
const CHART_HEIGHT = 160;

function periodSuffix(bucketMode: ReturnType<typeof getTrendBucketMode>): string {
  if (bucketMode === 'weekly') return ' this week';
  if (bucketMode === 'hourly') return ' this hour';
  if (bucketMode === '5min') return ' in 5 min';
  return '';
}

const ConnectionTrendChart: React.FC<ConnectionTrendChartProps> = ({
  createdAtValues,
  className = '',
}) => {
  const { dayCount } = useDashboardTrendDays();

  const points = useMemo(
    () => buildConnectionTrend(createdAtValues, dayCount),
    [createdAtValues, dayCount],
  );

  const bucketMode = getTrendBucketMode(dayCount);
  const axisLabels = useMemo(
    () => getChartAxisEndpointLabels(points, bucketMode),
    [points, bucketMode],
  );
  const unitLabel = getTrendChartUnitLabel(dayCount);

  const { displayHoverIndex, updateHoverAtIndex, clearHover, isHoverSource } = useSyncedChartHover(
    'network-reach',
    dayCount,
    bucketMode,
    points,
  );

  const todayX = useMemo(() => {
    const idx = findCurrentPeriodIndex(points, dayCount);
    if (idx == null) return null;
    return chartXAtPeriodIndex(idx, points.length, CHART_WIDTH);
  }, [points, dayCount]);

  const { coords, linePath, areaPath, maxY } = useMemo(() => {
    if (points.length === 0) {
      return {
        coords: [] as { x: number; y: number; point: ConnectionTrendPoint }[],
        linePath: '',
        areaPath: '',
        maxY: 1,
      };
    }

    const values = points.map((p) => p.added);
    const minV = 0;
    const maxV = Math.max(...values, 1);
    const range = Math.max(maxV - minV, 1);

    const nextCoords = points.map((p, i) => {
      const x =
        points.length <= 1 ? CHART_WIDTH / 2 : (i / (points.length - 1)) * CHART_WIDTH;
      const y = CHART_HEIGHT - ((p.added - minV) / range) * CHART_HEIGHT;
      return { x, y, point: p };
    });

    const line = nextCoords
      .map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`)
      .join(' ');
    const area = `${line} L ${nextCoords[nextCoords.length - 1].x.toFixed(1)} ${CHART_HEIGHT.toFixed(1)} L ${nextCoords[0].x.toFixed(1)} ${CHART_HEIGHT.toFixed(1)} Z`;

    return {
      coords: nextCoords,
      linePath: line,
      areaPath: area,
      maxY: maxV,
    };
  }, [points]);

  const hovered = displayHoverIndex != null ? points[displayHoverIndex] : null;
  const hoverXPercent =
    displayHoverIndex != null && coords[displayHoverIndex]
      ? (coords[displayHoverIndex].x / CHART_WIDTH) * 100
      : null;

  const pickNearestIndex = (clientX: number, rect: DOMRect) => {
    if (coords.length === 0) return null;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const xInViewBox = ratio * CHART_WIDTH;
    let nearest = 0;
    let minDist = Infinity;
    coords.forEach((c, i) => {
      const dist = Math.abs(c.x - xInViewBox);
      if (dist < minDist) {
        minDist = dist;
        nearest = i;
      }
    });
    return nearest;
  };

  if (points.length === 0) {
    return (
      <div className={`flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 ${className}`}>
        No trend data yet
      </div>
    );
  }

  return (
    <div className={`relative w-full min-w-0 min-h-[7.5rem] ${className}`}>
      <div className="absolute inset-0 z-0">
        <svg
          viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
          preserveAspectRatio="none"
          className="w-full h-full block cursor-crosshair"
          role="img"
          aria-label={
            bucketMode === '5min'
              ? 'Connections added per 5 minutes over the last hour'
              : bucketMode === 'hourly'
                ? 'Connections added per hour over the last 6 hours'
                : bucketMode === 'weekly'
                  ? `Connections added per week over the last ${dayCount} days`
                  : `Connections added per day over the last ${dayCount} days`
          }
          onMouseMove={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            updateHoverAtIndex(pickNearestIndex(e.clientX, rect));
          }}
          onMouseLeave={clearHover}
        >
          <defs>
            <linearGradient id="connectionTrendFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(16 185 129)" stopOpacity="0.35" />
              <stop offset="100%" stopColor="rgb(16 185 129)" stopOpacity="0.02" />
            </linearGradient>
          </defs>
          {coords.map((c, i) =>
            i > 0 ? (
              <line
                key={`day-grid-${c.point.periodStart}`}
                x1={c.x}
                y1={0}
                x2={c.x}
                y2={CHART_HEIGHT}
                stroke="rgb(203 213 225)"
                strokeWidth="1"
                strokeOpacity={c.point.isWeekStart ? 0.75 : 0.4}
                vectorEffect="non-scaling-stroke"
              />
            ) : null,
          )}
          <path d={areaPath} fill="url(#connectionTrendFill)" />
          <path
            d={linePath}
            fill="none"
            stroke="rgb(16 185 129)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <ChartTodayMarker x={todayX} height={CHART_HEIGHT} />
          {displayHoverIndex != null && coords[displayHoverIndex] ? (
            <ChartHoverHighlight
              x={coords[displayHoverIndex].x}
              height={CHART_HEIGHT}
              dots={[{ y: coords[displayHoverIndex].y, fill: 'rgb(16 185 129)' }]}
            />
          ) : null}
        </svg>
      </div>

      <div className="absolute top-1.5 left-2 right-2 z-10 pointer-events-none">
        <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 drop-shadow-sm">
          Growth{unitLabel ? ` · ${unitLabel}` : ''}
        </span>
      </div>

      {isHoverSource && hovered && hoverXPercent != null ? (
        <div
          className="pointer-events-none absolute top-7 z-20 -translate-x-1/2 rounded-md border border-gray-200 dark:border-gray-600 bg-white/95 dark:bg-gray-800/95 px-2 py-1 shadow-sm whitespace-nowrap backdrop-blur-sm"
          style={{ left: `${hoverXPercent}%` }}
        >
          <div className="text-[10px] font-medium text-gray-900 dark:text-gray-100">
            {formatTrendPeriodTooltip(hovered.periodStart, bucketMode)}
          </div>
          <div className="text-[10px] tabular-nums text-gray-600 dark:text-gray-300">
            {hovered.isFuture ? (
              <span className="text-gray-500 dark:text-gray-400">No data yet</span>
            ) : (
              <>
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {hovered.added.toLocaleString()}
                </span>{' '}
                added{periodSuffix(bucketMode)}
              </>
            )}
          </div>
        </div>
      ) : null}

      <div className="absolute bottom-1 left-2 right-2 z-10 flex justify-between text-[9px] text-gray-400/90 dark:text-gray-500 tabular-nums pointer-events-none">
        <span className="drop-shadow-sm">{axisLabels.start}</span>
        <span className="drop-shadow-sm">{axisLabels.end}</span>
      </div>

      <span className="sr-only">
        Peak daily additions: {maxY.toLocaleString()} over the chart period.
      </span>
    </div>
  );
};

export default ConnectionTrendChart;
